import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["saveButton", "resetButton", "table", "loadingOverlay", "statusIndicator"]
  static values = {
    calculateUrl: String,
    saveUrl: String,
    originalData: Object
  }

  connect() {
    console.log("Editable rate sheet controller connected")

    // Initialize state
    this.workingData = this.deepClone(this.originalDataValue)
    this.hasChanges = false
    this.pendingCalculation = null

    // Setup auto-calculation debouncing
    this.calculateDebounced = this.debounce(this.calculateSheet.bind(this), 500)

    // Initialize UI state
    this.updateSaveButtonState()
  }

  // Called by individual cell controllers when values change
  cellValueChanged(fieldPath, newValue) {
    console.log(`Cell changed: ${fieldPath} = ${newValue}`)
    this.setWorkingValue(fieldPath, newValue)
    this.markAsChanged()
    this.calculateDebounced()
  }

  setWorkingValue(fieldPath, value) {
    // Update working data structure
    // e.g., "Benefit Aspects.Basic Life.A - All.Proposed Rate" = 25.50
    value = `${value}`
    const pathParts = fieldPath.split('.')

    console.log(`setWorkingValue called with fieldPath: ${fieldPath}, value: ${value}`)
    console.log(`pathParts:`, pathParts)

    // Special handling for class and family type fields that affect the aspect key
    if (pathParts[3] === 'Class' || pathParts[3] === 'Family Type') {
      console.log(`Special handling for ${pathParts[3]} field`)
      this.updateAspectKey(pathParts, value)
    } else {
      // Normal field update - use the exact path as provided
      console.log(`Normal field update for ${fieldPath}`)
      let current = this.workingData

      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) current[pathParts[i]] = {}
        current = current[pathParts[i]]
      }
      current[pathParts[pathParts.length - 1]] = value

      console.log(`Updated working data:`, JSON.stringify(this.workingData, null, 2))
    }
  }

  updateAspectKey(pathParts, newValue) {
    // When class or family type changes, we need to update the aspect key
    // pathParts = ["Benefit Aspects", "Basic Life", "A - All", "Class"]
    const sectionName = pathParts[0]
    const benefitName = pathParts[1]
    const oldAspectKey = pathParts[2]
    const fieldName = pathParts[3]

    console.log(`updateAspectKey called with:`, {
      sectionName, benefitName, oldAspectKey, fieldName, newValue
    })

    // Get current values for this aspect
    const currentAspect = this.workingData[sectionName]?.[benefitName]?.[oldAspectKey] || {}
    console.log(`Current aspect data:`, currentAspect)

    // Update the field being changed
    currentAspect[fieldName] = newValue

    // Rebuild the aspect key based on current class and family type values
    const classValue = currentAspect['Class'] || ''
    const familyTypeValue = currentAspect['Family Type'] || ''

    console.log(`Class value: "${classValue}", Family Type value: "${familyTypeValue}"`)

    // Use the same logic as the backend: join non-empty values with " - "
    const aspectKeyParts = []
    if (classValue && classValue !== 'N/A' && !['Total', 'All'].includes(classValue)) {
      aspectKeyParts.push(classValue)
    }
    if (familyTypeValue && familyTypeValue !== 'N/A' && !['Total', 'All'].includes(familyTypeValue)) {
      aspectKeyParts.push(familyTypeValue)
    }

    const newAspectKey = aspectKeyParts.length === 0 ? 'All' : aspectKeyParts.join(' - ')

    console.log(`Aspect key parts:`, aspectKeyParts, `New aspect key: "${newAspectKey}"`)

    // Initialize the working data structure if needed
    if (!this.workingData[sectionName]) this.workingData[sectionName] = {}
    if (!this.workingData[sectionName][benefitName]) this.workingData[sectionName][benefitName] = {}

    // If the aspect key changed, move the data to the new key
    if (newAspectKey !== oldAspectKey) {
      console.log(`Moving data from "${oldAspectKey}" to "${newAspectKey}"`)
      this.workingData[sectionName][benefitName][newAspectKey] = currentAspect
      // Remove the old key if it exists and is different
      if (this.workingData[sectionName][benefitName][oldAspectKey]) {
        delete this.workingData[sectionName][benefitName][oldAspectKey]
      }
    } else {
      // Same key, just update the values
      console.log(`Updating existing key "${oldAspectKey}"`)
      this.workingData[sectionName][benefitName][oldAspectKey] = currentAspect
    }

    console.log(`Updated aspect key from "${oldAspectKey}" to "${newAspectKey}" for ${fieldName}=${newValue}`)
    console.log(`Working data after aspect key update:`, JSON.stringify(this.workingData, null, 2))
  }

  async calculateSheet() {
    if (this.pendingCalculation) {
      this.pendingCalculation.abort()
    }

    this.showCalculating()

    try {
      this.pendingCalculation = fetch(this.calculateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          working_data: this.workingData
        })
      })

      const response = await this.pendingCalculation
      const result = await response.json()

      if (result.status === 'success') {
        this.updateCalculatedFields(result.calculated_rows)
        this.showCalculationSuccess()
      } else {
        this.showCalculationError(result.message)
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Calculation failed:', error)
        this.showCalculationError('Calculation failed. Please try again.')
      }
    } finally {
      this.hideCalculating()
      this.pendingCalculation = null
    }
  }

  updateCalculatedFields(calculatedRows) {
    console.log('Updating calculated fields with:', calculatedRows)

    // Update DOM with new calculated values
    calculatedRows.forEach((row, index) => {
      const rowElement = this.findRowElement(index)
      if (rowElement) {
        this.updateRowCalculations(rowElement, row)
      } else {
        console.warn(`Could not find row element for index ${index}`)
      }
    })
  }

  findRowElement(index) {
    return this.tableTarget.querySelector(`[data-row-index="${index}"]`)
  }

  updateRowCalculations(rowElement, row) {
    console.log('Updating row calculations for row:', row)
    console.log('Row element:', rowElement)

    // Update percentage change (column 6)
    const percentageCell = rowElement.querySelector('[data-column="6"] .value-display')
    console.log('Percentage cell found:', percentageCell)

    if (percentageCell && row[6] !== null && row[6] !== undefined) {
      const oldValue = percentageCell.textContent
      const formattedPercentage = typeof row[6] === 'number' ?
        `${row[6].toFixed(2)}%` : row[6]
      percentageCell.textContent = formattedPercentage
      console.log(`Updated percentage cell from "${oldValue}" to: "${formattedPercentage}"`)

      // Update color based on positive/negative
      // const cellContainer = percentageCell.closest('td')
      // cellContainer.classList.remove('text-red-600', 'text-green-600', 'text-gray-900')
      // if (typeof row[6] === 'number') {
      //   if (row[6] > 0) {
      //     cellContainer.classList.add('text-red-600')
      //     console.log('Added red color for positive percentage')
      //   } else if (row[6] < 0) {
      //     cellContainer.classList.add('text-green-600')
      //     console.log('Added green color for negative percentage')
      //   } else {
      //     cellContainer.classList.add('text-gray-900')
      //     console.log('Added gray color for zero percentage')
      //   }
      // }
    } else {
      console.warn('Could not find percentage cell or value is null/undefined', {
        percentageCell,
        rowValue: row[6]
      })
    }

    // Update all calculated/display columns that might change
    [7, 8, 9].forEach(columnIndex => {
      const calcCell = rowElement.querySelector(`[data-column="${columnIndex}"] .value-display`)
      console.log(`Looking for column ${columnIndex} cell:`, calcCell)
      console.log(`Row value for column ${columnIndex}:`, row[columnIndex])

      if (calcCell && row[columnIndex] !== null && row[columnIndex] !== undefined) {
        const oldValue = calcCell.textContent
        let formattedValue

        if (columnIndex === 7 || columnIndex === 9) {
          // Low and High columns typically show "-"
          formattedValue = row[columnIndex] === '-' ? '-' : row[columnIndex]
        } else if (columnIndex === 8) {
          // Mid column shows calculated values
          formattedValue = typeof row[columnIndex] === 'number' ?
            this.formatNumberWithPrecision(row[columnIndex]) : row[columnIndex]
        }

        calcCell.textContent = formattedValue
        console.log(`Updated column ${columnIndex} from "${oldValue}" to: "${formattedValue}"`)
      } else {
        console.warn(`Could not update column ${columnIndex}`, {
          calcCell,
          rowValue: row[columnIndex],
          isNull: row[columnIndex] === null,
          isUndefined: row[columnIndex] === undefined
        })
      }
    })

    // Also update any total/summary row values (columns 4 and 5 for total rows)
    const isTotalRow = this.isTotalRow(row)
    if (isTotalRow) {
      [4, 5].forEach(columnIndex => {
        const totalCell = rowElement.querySelector(`[data-column="${columnIndex}"] .value-display`)
        if (totalCell && row[columnIndex] !== null && row[columnIndex] !== undefined) {
          const formattedValue = typeof row[columnIndex] === 'number' ?
            this.formatCurrency(row[columnIndex]) : row[columnIndex]
          totalCell.textContent = formattedValue
          console.log(`Updated total column ${columnIndex} to: ${formattedValue}`)
        }
      })
    }
  }

  isTotalRow(row) {
    const totalRowTypes = ["Pooled Premium", "Experience Rated Premium", "Total Premium Monthly", "Total Premium Annual", "Annual Difference"]
    return totalRowTypes.includes(row[0]) || row[1] === "Total"
  }

  formatNumberWithPrecision(value) {
    // Check if this appears to be a currency value (for total rows) or rate value
    if (value > 1000) {
      // Likely a currency value, use currency formatting
      return this.formatCurrency(value)
    } else {
      // Likely a rate value, use precision formatting
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      }).format(value)
    }
  }

  async saveSheet() {
    if (this.saveButtonTarget.disabled) return
    this.saveButtonTarget.disabled = true
    this.showSaving()

    try {
      const response = await fetch(this.saveUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          field_updates: this.buildFieldUpdates()
        })
      })

      const result = await response.json()

      if (result.status === 'success') {
        this.originalDataValue = this.deepClone(this.workingData)
        this.markAsSaved()
        this.showSaveSuccess()
      } else {
        this.showSaveError(result.errors || ['Save failed'])
      }
    } catch (error) {
      console.error('Save failed:', error)
      this.showSaveError(['Network error occurred'])
    } finally {
      this.saveButtonTarget.disabled = false
      this.hideSaving()
    }
  }

  resetSheet() {
    if (this.resetButtonTarget.disabled) return
    this.workingData = this.deepClone(this.originalDataValue)
    this.hasChanges = false
    this.restoreOriginalValues()
    this.calculateSheet() // Recalculate with original values
    this.updateSaveButtonState()
  }

  buildFieldUpdates() {
    const updates = {}

    // Compare working data with original data to find changes
    this.findChangedFields(this.workingData, this.originalDataValue, '', updates)

    return updates
  }

  findChangedFields(working, original, basePath, updates) {
    Object.keys(working).forEach(key => {
      const currentPath = basePath ? `${basePath}.${key}` : key

      if (typeof working[key] === 'object' && working[key] !== null) {
        if (typeof original[key] === 'object' && original[key] !== null) {
          this.findChangedFields(working[key], original[key], currentPath, updates)
        } else {
          // New object added
          this.findChangedFields(working[key], {}, currentPath, updates)
        }
      } else {
        // Compare primitive values
        if (working[key] !== original[key]) {
          updates[currentPath] = working[key]
        }
      }
    })
  }

  restoreOriginalValues() {
    // Restore all editable cells to their original values
    const editableCells = this.element.querySelectorAll('[data-controller*="editable-cell"]')
    editableCells.forEach(cell => {
      const cellController = this.application.getControllerForElementAndIdentifier(cell, 'editable-cell')
      if (cellController && cellController.restoreOriginalValue) {
        cellController.restoreOriginalValue()
      }
    })
  }

  markAsChanged() {
    this.hasChanges = true
    this.updateSaveButtonState()
  }

  markAsSaved() {
    this.hasChanges = false
    this.updateSaveButtonState()

    // Remove change indicators from all cells
    const changedCells = this.element.querySelectorAll('.has-changes')
    changedCells.forEach(cell => {
      cell.classList.remove('has-changes', 'bg-yellow-50', 'border-l-4', 'border-orange-400')
    })
  }

  updateSaveButtonState() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = !this.hasChanges

      if (this.hasChanges) {
        //this.saveButtonTarget.classList.add('bg-green-600', 'hover:bg-green-700')
        //this.saveButtonTarget.classList.remove('bg-gray-400')
        this.saveButtonTarget.classList.remove('button__disabled')
        this.resetButtonTarget.classList.remove('button__disabled')
        this.saveButtonTarget.disabled = false
        this.resetButtonTarget.disabled = false
      } else {
        //this.saveButtonTarget.classList.remove('bg-green-600', 'hover:bg-green-700')
        //this.saveButtonTarget.classList.add('bg-gray-400')
        this.saveButtonTarget.classList.add('button__disabled')
        this.resetButtonTarget.classList.add('button__disabled')
        this.saveButtonTarget.disabled = true
        this.resetButtonTarget.disabled = true
      }
    }
  }

  // UI State Methods
  showCalculating() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove('hidden')
    }
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = 'Calculating...'
      this.statusIndicatorTarget.className = 'text-sm text-blue-600'
    }
  }

  hideCalculating() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add('hidden')
    }
  }

  showCalculationSuccess() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = 'Calculations updated'
      this.statusIndicatorTarget.className = 'text-sm text-green-600'
      setTimeout(() => {
        this.statusIndicatorTarget.textContent = ''
      }, 3000)
    }
  }

  showCalculationError(message) {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = `Calculation error: ${message}`
      this.statusIndicatorTarget.className = 'text-sm text-red-600'
    }
  }

  showSaving() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = 'Saving...'
      this.statusIndicatorTarget.className = 'text-sm text-blue-600'
    }
  }

  hideSaving() {
    // Status will be updated by success/error methods
  }

  showSaveSuccess() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = 'Changes saved successfully'
      this.statusIndicatorTarget.className = 'text-sm text-green-600'
      setTimeout(() => {
        this.statusIndicatorTarget.textContent = ''
      }, 3000)
    }
  }

  showSaveError(errors) {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = `Save failed: ${errors.join(', ')}`
      this.statusIndicatorTarget.className = 'text-sm text-red-600'
    }
  }

  // Utility Methods
  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj))
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 3
    }).format(value)
  }
}
